import type { Block } from './blocks';
import type { BlockSchedules, BlockWindow } from '../dataProvider';

/**
 * Per-block scheduling derivations (D-page-schema-editor-8d). The visible state is
 * PURE and DERIVED off an injected clock, never stored, the promoLifecycle /
 * ruleLifecycle idiom (a (window, now) => state). The window lives ALONGSIDE the
 * block (a blockSchedules map keyed by block id), so the mirrored BlockSchema (the
 * apps' render union) stays a faithful copy; the real backend filters server-side at
 * serve, the migration seam.
 */

/** The window-only lifecycle subset (a block has no draft / paused status of its own). */
export type BlockScheduleState = 'scheduled' | 'live' | 'expired';

/**
 * The derived visible state for one block at `now`. No window (or open bounds, or in
 * window) is LIVE; a future visibleFrom is SCHEDULED; a passed visibleUntil is EXPIRED.
 * Scheduled takes precedence over expired when both bounds somehow exclude now.
 */
export const blockScheduleState = (
  window: BlockWindow | undefined,
  now: number,
): BlockScheduleState => {
  if (!window) return 'live';
  if (window.visibleFrom != null && now < Date.parse(window.visibleFrom)) {
    return 'scheduled';
  }
  if (window.visibleUntil != null && now > Date.parse(window.visibleUntil)) {
    return 'expired';
  }
  return 'live';
};

export const SCHEDULE_LABEL: Record<BlockScheduleState, string> = {
  scheduled: 'Scheduled',
  live: 'Live',
  expired: 'Expired',
};

export const SCHEDULE_COLOR: Record<
  BlockScheduleState,
  'info' | 'success' | 'warning'
> = {
  scheduled: 'info',
  live: 'success',
  expired: 'warning',
};

/** Is this block visible at `now` (the as-of filter predicate)? */
export const isBlockVisible = (
  window: BlockWindow | undefined,
  now: number,
): boolean => blockScheduleState(window, now) === 'live';

/** The blocks visible at `now` given their schedules (the as-of preview filter). */
export const visibleBlocks = (
  blocks: Block[],
  schedules: BlockSchedules,
  now: number,
): Block[] => blocks.filter((b) => isBlockVisible(schedules[b.id], now));

/** Does a window set any bound (so it is worth showing on a timeline)? */
export const hasWindow = (window: BlockWindow | undefined): boolean =>
  window != null && (window.visibleFrom != null || window.visibleUntil != null);

/** "from 2026-09-01 to 2026-12-26", "from 2026-09-01", "until 2026-12-26" or "always". */
export const formatBlockWindow = (window: BlockWindow | undefined): string => {
  if (!hasWindow(window)) return 'Always visible';
  const from = window?.visibleFrom ? window.visibleFrom.slice(0, 10) : null;
  const to = window?.visibleUntil ? window.visibleUntil.slice(0, 10) : null;
  if (from && to) return `from ${from} to ${to}`;
  if (from) return `from ${from}`;
  return `until ${to}`;
};
